import qz from "qz-tray";

let setupDone = false;
let connectPromise = null;
export let lastQzError = "";

function setupQzOnce() {
  if (setupDone) return;
  setupDone = true;
  try {
    if (typeof qz.api?.setPromiseType === "function") {
      qz.api.setPromiseType((resolver) => new Promise(resolver));
    }
  } catch {
    /* QZ 2.2 ha già Promise native */
  }
  // Come Emergent: unsigned. resolve(null) + SHA512 fa fallire il websocket anche se QZ è in ascolto.
  try {
    qz.security.setCertificatePromise((resolve) => resolve());
    qz.security.setSignaturePromise(() => (resolve) => resolve());
  } catch {
    /* ignore */
  }
}

export const isQzActive = () => {
  try {
    return !!qz.websocket.isActive();
  } catch {
    return false;
  }
};

/**
 * Origine pubblica (veltaone.it o IP VPS) → solo localhost.qz.io.
 * ws://127.0.0.1 da un sito pubblico Chrome lo tiene in pending (Private Network Access): nessun popup Allow.
 */
export const connectQz = async () => {
  setupQzOnce();
  lastQzError = "";
  if (isQzActive()) return true;
  if (connectPromise) return connectPromise;

  const pageHost = typeof window !== "undefined" ? window.location.hostname : "";
  const localPage = pageHost === "localhost" || pageHost === "127.0.0.1";
  const opts = localPage
    ? {
        host: ["localhost", "127.0.0.1"],
        usingSecure: typeof window !== "undefined" && window.location.protocol === "https:",
        retries: 3,
        delay: 1,
        keepAlive: 60,
      }
    : {
        host: "localhost.qz.io",
        usingSecure: true,
        retries: 4,
        delay: 1,
        keepAlive: 60,
      };

  connectPromise = qz.websocket
    .connect(opts)
    .then(() => true)
    .catch((e) => {
      lastQzError = String(e?.message || e || "QZ non raggiungibile");
      return false;
    })
    .finally(() => {
      connectPromise = null;
    });

  return connectPromise;
};

export const qzPrintRaw = async (printer, data, jobName = "VELTA", encoding = "CP858") => {
  const ok = await connectQz();
  if (!ok) throw new Error(lastQzError || "QZ_NOT_CONNECTED");
  const target = printer.ip ? { host: printer.ip, port: printer.port || 9100 } : printer.name;
  const config = qz.configs.create(target, { encoding, jobName, copies: 1 });
  await qz.print(config, [{ type: "raw", format: "command", flavor: "plain", data }]);
};

let socketCbInstalled = false;
const socketHandlers = new Map();

const installSocketCallbacks = () => {
  if (socketCbInstalled) return;
  if (!qz.socket || typeof qz.socket.setSocketCallbacks !== "function") return;
  socketCbInstalled = true;
  qz.socket.setSocketCallbacks((evt) => {
    const h = socketHandlers.get(`${evt.host}:${evt.port}`);
    if (!h) return;
    if (evt.exception) h.onError(new Error(evt.exception));
    else if (evt.response != null) h.onData(String(evt.response));
  });
};

async function qzRawSocket(host, port, rawRequest, { timeoutMs = 12000, doneWhen, encoding = "ISO-8859-1" } = {}) {
  const ok = await connectQz();
  if (!ok) throw new Error(lastQzError || "QZ_NOT_CONNECTED");
  if (!qz.socket || typeof qz.socket.open !== "function") {
    await qzPrintRaw({ ip: host, port }, rawRequest, "VELTA-SOCK", encoding === "UTF-8" ? "UTF-8" : "CP858");
    return "";
  }
  installSocketCallbacks();
  const key = `${host}:${port}`;
  let text = "";
  let settled = false;
  let timer;
  const response = new Promise((resolve, reject) => {
    const finish = (fn, v) => {
      if (!settled) {
        settled = true;
        clearTimeout(timer);
        fn(v);
      }
    };
    timer = setTimeout(() => finish(resolve, text), timeoutMs);
    socketHandlers.set(key, {
      onData: (line) => {
        text += (text ? "\n" : "") + line;
        if (doneWhen && doneWhen(text)) finish(resolve, text);
      },
      onError: (e) => (text ? finish(resolve, text) : finish(reject, e)),
    });
  });
  try {
    await qz.socket.close(host, port);
  } catch {
    /* nessuna socket precedente */
  }
  try {
    const openSend = (async () => {
      await qz.socket.open(host, port, { encoding });
      await qz.socket.sendData(host, port, rawRequest);
      return await response;
    })();
    const timed = new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`timeout ${timeoutMs}ms verso ${host}:${port}`)), timeoutMs + 400);
    });
    return await Promise.race([openSend, timed]);
  } finally {
    clearTimeout(timer);
    socketHandlers.delete(key);
    try {
      await qz.socket.close(host, port);
    } catch {
      /* già chiusa */
    }
  }
}

export const qzSocketExchange = async (host, port, rawRequest, opts = {}) =>
  qzRawSocket(host, port, rawRequest, { encoding: "ISO-8859-1", ...opts });

export const qzHttpRequest = async (host, port, rawRequest, opts = {}) =>
  qzRawSocket(host, port, rawRequest, { encoding: "UTF-8", ...opts });

export const qzListSystemPrinters = async () => {
  const ok = await connectQz();
  if (!ok) return [];
  try {
    return await qz.printers.find();
  } catch {
    return [];
  }
};
